import React, { useState, useEffect } from "react";
import { Paper, Box, Typography, Grid, FormHelperText, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import dayjs from "dayjs";
import { industriesRequest } from "../../../remote/industriesRequest";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import AddressAutoComplete from "../../../components/FormComponents/AddressAutoComplete";
import TypeSelect from "../../../components/FormComponents/TypeDropdown";
import CustomDropdown from "../../../components/FormComponents/CustomDropdown";
import CustomTextField from "../../../components/FormComponents/CustomTextField";
import CustomDatePicker from "../../../components/FormComponents/CustomDatePicker"
import RadioSelect from "../../../components/FormComponents/RadioSelect";
import PayInput from "../../../components/FormComponents/PayInput"
import FormButtonLayout from "../../../components/LayoutComponents/FormButtonLayout";
import { partnersRequest } from "../../../remote/partnersRequest";
import { employmentTypes, payTypes, seniorityLevelTypes } from "../../../staticDataSet";


export default function JobProfileForm({ formData, skillProfiles, setFormData, onSave, onDelete, session, loading }) {
    const [error, setError] = useState({});
    const [addressParts, setAddressParts] = useState({
        address1: "",
        address2: "",
        city: "",
        state: "",
        country: "",
        zip: "",
    });

    useEffect(() => {
       let selectedProfile = skillProfiles?.find(sp => sp.profileId === formData?.templateId) || null;
        handleChange("industryId", selectedProfile?.industryId);
        handleChange("dutiesResponsibilities", selectedProfile?.dutiesResponsibilities);
        handleChange("summary", selectedProfile?.profileDescription);
    }, [formData?.templateId, skillProfiles]);


    const handleSave = () => {
        const errors = {};

        if (!formData?.profileName || formData.profileName.trim() === "") {
            errors.profileName = "Profile Name is required";
        }

        if (!formData?.summary || formData.summary.trim() === "") {
            errors.summary = "Summary is required";
        }

        if (!formData.effectiveStartDate) {
            errors.effectiveStartDate = "Effective Start Date is required."
        }
        if (formData?.effectiveStartDate && !formData?.effectiveEndDate) {
            errors.effectiveEndDate = "Effective End Date is required when a start date is chosen.";
        }
        if (
            formData?.effectiveStartDate &&
            formData?.effectiveEndDate &&
            new Date(formData.effectiveStartDate) > new Date(formData.effectiveEndDate)
        ) {
            errors.effectiveEndDate = "Effective End Date cannot be before start date.";
        }
        if (!formData?.entityId) {
            errors.entityId = "Company is required."
        }

        if (!formData?.templateId) {
            errors.templateId = "Skill Profile is required."
        }
        if (!formData.payMin) {
            errors.payMin = "Min Pay required."
        }
        if (!formData.location || formData.location.trim() === "") {
            errors.location = "Location is required."
        }
        if (!formData.seniorityLevel || formData.seniorityLevel.trim() === "") {
            errors.seniorityLevel = "Seniority is required."
        }
        if (!formData.employmentType || formData.employmentType.trim() === "") {
            errors.employmentType = "Employment Type is required."
        }
        if (!formData.industryId) {
            errors.industryId = "Industry is required."
        }
        if (!formData.payType || formData.payType.trim() === "") {
            errors.payType = "Pay type is required."
        }

        if (Object.keys(errors).length > 0) {
            setError(errors);
            return;
        }
        setError({});
        onSave();
    };


    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    useEffect(() => {
        const composed = [
            addressParts.address1,
            addressParts.address2,
            addressParts.city,
            addressParts.state,
            addressParts.country,
            addressParts.zip,
        ]
            .filter(Boolean)
            .join(", ");

        setFormData((prev) => ({ ...prev, location: composed }));
    }, [addressParts]);

    return (
        <>
            <Paper sx={{ p: 3, display: "flex", flexDirection: "column", maxHeight: "60vh", overflowY: "auto" }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                        Job Profile Details
                    </Typography>
                    {loading && <LoadingCircle size={20} />}
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            formData={formData}
                            field='profileName'
                            handleChange={handleChange}
                            required={true}
                            label={"Profile Name"}
                            error={error.profileName}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='summary'
                            handleChange={handleChange}
                            required={true}
                            multiline={true}
                            rows={4}
                            label={"Summary"}
                            error={error.summary}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RadioSelect value={formData?.activeFlag} label={formData.activeFlag ? "Active" : "Not Active"} onChange={(val) => handleChange("activeFlag", val)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={dayjs(formData?.effectiveStartDate)}
                            setSelectedDate={(value) => setFormData((prevState) => ({ ...prevState, effectiveStartDate: value }))}
                            label="Effective Start Date"
                            error={!!error.effectiveStartDate} />
                        <FormHelperText>{!!error.effectiveStartDate}</FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={dayjs(formData?.effectiveEndDate)}
                            setSelectedDate={(value) => setFormData((prevState) => ({ ...prevState, effectiveEndDate: value }))}
                            label="Effective End Date"
                            error={!!error.effectiveEndDate} />
                        <FormHelperText>{!!error.effectiveStartDate}</FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            value={formData?.entityId}
                            entityName="entityName"
                            entityIdField="entityId"
                            label="Company"
                            setEntity={(id) => handleChange("entityId", id)}
                            style={{ width: '100%' }}
                            error={!!error.entityId}
                            helperText={error.entityId}
                            request={partnersRequest}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!error.templateId}>
                            <InputLabel id="skill-profile-label">Skill Profile</InputLabel>
                            <Select
                                labelId="skill-profile-label"   
                                value={formData?.templateId || ""}
                                label="Skill Profile"
                                onChange={(e) => handleChange("templateId", e.target.value)}
                                style={{ width: '100%' }}
                                variant="standard"
                            >
                                {skillProfiles?.map((profile) => (
                                    <MenuItem key={profile.profileId} value={profile.profileId}>
                                        {profile.profileName}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{error.templateId}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!error.payMin}>
                            <PayInput value={formData.payMin} onChange={(e) => handleChange("payMin", e)} label="Min Pay" />
                            <FormHelperText>{error.payMin}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!error.payMin}>
                            <PayInput value={formData.payMax} onChange={(e) => handleChange("payMax", e)} label="Max Pay" />
                            <FormHelperText>{error.payMax}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <AddressAutoComplete
                            address1={addressParts.address1 || formData?.location}
                            setAddress1={(value) => setAddressParts((p) => ({ ...p, address1: value }))}
                            setAddress2={(value) => setAddressParts((p) => ({ ...p, address2: value }))}
                            setCity={(value) => setAddressParts((p) => ({ ...p, city: value }))}
                            setState={(value) => setAddressParts((p) => ({ ...p, state: value }))}
                            setCountry={(value) => setAddressParts((p) => ({ ...p, country: value }))}
                            setZip={(value) => setAddressParts((p) => ({ ...p, zip: value }))}
                            error={!!error.location}
                            helperText={error.location}
                            required={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TypeSelect
                            value={formData?.employmentType || ""}
                            entities={employmentTypes}
                            onChange={(type) => handleChange('employmentType', type)}
                            label={"Employment Type"}
                            error={!!error.employmentType}
                            helperText={error.employmentType}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TypeSelect
                            value={formData?.payType || ""}
                            entities={payTypes}
                            onChange={(type) => handleChange('payType', type)}
                            label={"Pay Type"}
                            helperText={error.payType}
                            error={!!error.payType}
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TypeSelect
                            value={formData?.seniorityLevel || ""}
                            onChange={(type) => handleChange('seniorityLevel', type)}
                            entities={seniorityLevelTypes}
                            label={"Seniority Level"}
                            error={!!error.seniorityLevel}
                            helperText={error.seniorityLevel}
                            variant="standard"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            value={formData?.industryId}
                            setEntity={(industryId) => handleChange("industryId", industryId)}
                            style={{ width: "100%" }}
                            label="Industry"
                            request={industriesRequest}
                            entityName="industryName"
                            entityIdField="industryId"
                            error={error.industryId}
                            helperText={error.industryId}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='dutiesResponsibilities'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Duties and Resposnibilities"}
                            error={error.dutiesResponsibilities}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='requiredEducation'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Required Education"}
                            error={error.requiredEducation}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='requiredExperience'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Required Experience"}
                            error={error.requiredExperience}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='requiredCertificates'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Required Certificates"}
                            error={error.requiredCertificates}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='requiredOther'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Required Other"}
                            error={error.requiredOther}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='preferredQualifications'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Preferred Qualifications"}
                            error={error.preferredQualifications}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='physicalDemands'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Physical Demands"}
                            error={error.physicalDemands}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='benefits'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Benefits"}
                            error={error.benefits}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='additionalInformation'
                            handleChange={handleChange}
                            multiline={true}
                            rows={4}
                            label={"Additional Information"}
                            error={error.additionalInformation}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            formData={formData}
                            field='linkAdditionalInformation'
                            handleChange={handleChange}
                            label={"Link Additional Information"}
                            error={error.linkAdditionalInformation}
                            type='url'
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            formData={formData}
                            field='linkApply'
                            handleChange={handleChange}
                            label={"Link Apply"}
                            error={error.linkApply}
                            type='url'
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            formData={formData}
                            field='workEnvironment'
                            handleChange={handleChange}
                            label={"Work Environment"}
                            multiline={true}
                            rows={4}
                            error={error.workEnvironment}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            formData={formData}
                            field='owner'
                            handleChange={handleChange}
                            label={"Owner"}
                            error={error.owner}
                            readOnly={true}
                        />
                    </Grid>
                </Grid>
            </Paper>
            <Box sx={{ mt: 2 }}>
                <FormButtonLayout
                    handleSave={handleSave}
                    onDelete={onDelete}
                    selected={formData.profileId > 0}
                />
            </Box>
        </>
    )
}